import * as React from "react";
import { useState } from "react";
import RangeSelector from "../../../components/range_selector/range_selector";
import { Card } from "../../../components/general/card";
import { Box, Grid } from "@mui/material";
import GeneralInfo from "../../../components/statistics_tab/general_statistics";
import BatteryInfo from "../../../components/statistics_tab/battery_statistics";
import SolarInfo from "../../../components/statistics_tab/solar_statisctics";
import PastOutages from "../../../components/statistics_tab/past_outages";
import SiteQualityRating from "../../../components/statistics_tab/site_quality_rating";
import Export from "components/statistics_tab/export";
import {
  fetchStatisticsGeneral,
  fetchStatisticsBattery,
  fetchStatisticsProduction,
  fetchOutages,
  fetchQualityRating,
} from "../../../scripts/api_v2/statistics_client";
import { useParams } from "react-router-dom";
import useBackend from "../../../hooks/use_backend";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";
import { endOfDay } from "date-fns";
import { toLocaleString } from "../../../scripts/utils";

export default function StatisticsTab({ required_privilege }) {
  const params = useParams();
  const backend = useBackend();
  const [range, setRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });

  // Only poll when the selected range includes today — historical data won't change
  const isCurrentRangeActive = endOfDay(new Date()).getTime() === endOfDay(range.endDate).getTime();

  let { data: generalData, isLoading: fetchingGeneral } = useQuery({
    queryKey: [REACT_QUERY_KEYS.STATISTICS_GENERAL, backend.fetch, params.site_id, range.startDate, range.endDate],
    queryFn: () => fetchStatisticsGeneral(backend.fetch, params.site_id, range.startDate, range.endDate),
    refetchInterval: isCurrentRangeActive ? 30000 : false,
  });

  let { data: batteryData, isLoading: fetchingBattery } = useQuery({
    queryKey: [REACT_QUERY_KEYS.STATISTICS_BATTERY, backend.fetch, params.site_id, range.startDate, range.endDate],
    queryFn: () => fetchStatisticsBattery(backend.fetch, params.site_id, range.startDate, range.endDate),
    refetchInterval: isCurrentRangeActive ? 30000 : false,
  });

  let { data: productionData, isLoading: fetchingProduction } = useQuery({
    queryKey: [REACT_QUERY_KEYS.STATISTICS_PRODUCTION, backend.fetch, params.site_id, range.startDate, range.endDate],
    queryFn: () => fetchStatisticsProduction(backend.fetch, params.site_id, range.startDate, range.endDate),
    refetchInterval: isCurrentRangeActive ? 30000 : false,
  });

  let { data: outagesData, isLoading: fetchingOutages } = useQuery({
    queryKey: [REACT_QUERY_KEYS.STATISTICS_OUTAGES, backend.fetch, params.site_id, range.startDate, range.endDate],
    queryFn: () => fetchOutages(backend.fetch, params.site_id, range.startDate, range.endDate),
    refetchInterval: isCurrentRangeActive ? 30000 : false,
  });

  let { data: qualityRatingData, isLoading: fetchingQualityRating } = useQuery({
    queryKey: [
      REACT_QUERY_KEYS.STATISTICS_QUALITY_RATING,
      backend.fetch,
      params.site_id,
      range.startDate,
      range.endDate,
    ],
    queryFn: () => fetchQualityRating(backend.fetch, params.site_id, range.startDate, range.endDate),
    refetchInterval: isCurrentRangeActive ? 30000 : false,
  });

  if (!params.site_id) return;

  return (
    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12}>
        <HeaderCard
          range={range}
          setRange={setRange}
          data={generalData}
          isFetching={fetchingGeneral}
          outages={outagesData}
        />
      </Grid>
      <Grid item xs={12}>
        <GeneralInfo
          productionData={productionData}
          generalData={generalData}
          isFetchingProductionData={fetchingProduction}
          isFetchingGeneralData={fetchingGeneral}
        />
      </Grid>
      {batteryData && (
        <Grid item xs={12}>
          <BatteryInfo data={batteryData} isFetching={fetchingBattery} />
        </Grid>
      )}
      <Grid item xs={12}>
        <SolarInfo data={productionData} isFetching={fetchingProduction} />
      </Grid>
      <Grid item xs={12}>
        <Export startDate={range.startDate} endDate={range.endDate} />
      </Grid>
      <Grid item md={5} xs={12} display="flex" flexDirection="column">
        <PastOutages data={outagesData} isFetching={fetchingOutages} />
      </Grid>
      <Grid item md={7} xs={12} display="flex" flexDirection="column">
        <SiteQualityRating data={qualityRatingData} isFetching={fetchingQualityRating} />
      </Grid>
    </Grid>
  );
}

function HeaderCard({ range, setRange, data, isFetching, outages }) {
  const last_updated = data?.last_updated ? Number(data.last_updated) : null;
  const last_updated_local = last_updated ? toLocaleString(last_updated) : "-";
  const outageCount = outages?.site_outages ? outages.site_outages.length : 0;

  return (
    <Card display="flex" flexDirection="column" justifyContent="center" alignItems="center">
      <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
        <RangeSelector range={range} setRange={setRange} maxRangeDays={null} />
      </Box>
      <p style={{ color: "grey" }}>Data was last updated {isFetching ? "-" : last_updated_local}</p>
      {outageCount !== 0 && (
        <p style={{
          color: "#d32f2f",
          backgroundColor: "#ffebee",
          padding: "12px 16px",
          borderRadius: "4px",
          border: "1px solid #d32f2f",
          fontWeight: "600",
          marginTop: "8px",
          fontSize: "14px"
        }}>
          Disclaimer: during this period there were some site outages. Data may slightly differ from reality due to these outages.
        </p>
      )}
    </Card>
  );
}
